import { Observable, Subject, Subscription, BehaviorSubject } from 'rxjs';

import { ClientCommand, ServerEvent, WsToolRequestEvent } from '../ws/agent-ws.types';
import { AgentTool, AgentToolResult } from '../tools/agent-tool.types';
import { ChatHistoryPageQuery, ChatSessionState } from 'src/app/cyn/chat-module/types/chat-session.types';
import { normalizeHistoryQuery } from '../utils/chat-pagination';

/** Провайдер команд и событий одной выбранной сессии агента. */
export class ChatAgentSessionProvider {
    /** Идентификатор сессии агента. */
    public readonly id: string;

    /** Поток событий протокола, относящихся к этой сессии. */
    public readonly events$: Observable<ServerEvent>;

    /** Состояние соединения с агентом (для UI). */
    public readonly connectionState$: Observable<ChatSessionState>;

    private readonly eventsSubject = new Subject<ServerEvent>();
    private readonly connectionStateSubject = new BehaviorSubject<ChatSessionState>('connecting');
    private readonly sendCommand: (command: ClientCommand) => void;
    private readonly sendBinary: (data: ArrayBuffer) => void;
    private readonly tools: AgentTool[];
    private lastHistoryQuery: ChatHistoryPageQuery = {};
    private readonly runningToolCalls = new Set<string>();
    private readonly toolResults = new Map<string, AgentToolResult>();
    private readonly toolAbort = new AbortController();
    private destroyed = false;

    public constructor(
        id: string,
        sendCommand: (command: ClientCommand) => void,
        sendBinary: (data: ArrayBuffer) => void,
        tools: AgentTool[] = [],
    ) {
        this.id = id;
        this.sendCommand = sendCommand;
        this.sendBinary = sendBinary;
        this.tools = tools;
        this.events$ = this.eventsSubject.asObservable();
        this.connectionState$ = this.connectionStateSubject.asObservable();
    }

    public setConnectionState(state: ChatSessionState): void {
        this.connectionStateSubject.next(state);
    }

    /**
     * Запросить страницу истории.
     * Без аргумента — повторить последний запрос (reconnect).
     * Пустой объект — хвост переписки. `limit` не шлём: размер страницы задаёт сервер.
     */
    public loadHistory(query?: ChatHistoryPageQuery): void {
        if (query !== undefined) {
            this.lastHistoryQuery = normalizeHistoryQuery(query);
        }

        this.sendCommand({
            type: 'get_history',
            ...this.lastHistoryQuery,
        });
    }

    /** Отправить текстовое сообщение агенту (опционально с вложениями). */
    public sendMessage(text: string, attachments?: { fileId: string }[]): void {
        this.sendCommand({
            type: 'send_message',
            text,
            page_context: { url: window.location.href, title: document.title },
            ...(attachments?.length ? { attachments } : {}),
        });
    }

    /** Отправить команду прерывания текущей генерации. */
    public cancel(): void {
        this.sendCommand({ type: 'cancel' });
    }

    /**
     * Отправить аудио потоком чанков.
     * Инициирует буфер командой `send_audio_start`, передаёт бинарные фреймы по мере прихода,
     * завершает буфер командой `send_audio_stop` при complete или error источника.
     * `attachments` — уже загруженные fileId, передаются вместе с `send_audio_stop`.
     */
    public sendAudio(
        chunks$: Observable<ArrayBuffer>,
        attachments?: { fileId: string }[],
    ): Subscription {
        this.sendCommand({ type: 'send_audio_start' });

        const stop = (includeAttachments: boolean): void => this.sendCommand({
            type: 'send_audio_stop',
            page_context: { url: window.location.href, title: document.title },
            ...(includeAttachments && attachments?.length ? { attachments } : {}),
        });

        return chunks$.subscribe({
            next: chunk => this.sendBinary(chunk),
            complete: () => stop(true),
            error: () => stop(false),
        });
    }

    /** Пробросить событие протокола в потребителя сессии. */
    public applyServerEvent(event: ServerEvent): void {
        if (event.kind === 'tool_request') {
            void this.handleToolRequest(event);
            return;
        }

        this.eventsSubject.next(event);
    }

    /** Освободить подписчиков сессии. */
    public destroy(): void {
        this.destroyed = true;
        this.toolAbort.abort();
        this.toolResults.clear();
        this.eventsSubject.complete();
        this.connectionStateSubject.complete();
    }

    private async handleToolRequest(event: WsToolRequestEvent): Promise<void> {
        if (this.destroyed || this.runningToolCalls.has(event.toolCallId)) return;
        this.runningToolCalls.add(event.toolCallId);

        console.log(`Handling tool request for tool "${event.toolName}" with call ID "${event.toolCallId}"`);
        const tool = this.tools.find(t => t.declaration.toolName === event.toolName);

        let result = this.toolResults.get(event.toolCallId);

        if (!result) {
            if (!tool) {
                result = { ok: false, error: { code: 'TOOL_NOT_FOUND', message: `Tool "${event.toolName}" is not registered` } };
            } else {
                try {
                    result = await tool.execute(event.args, {
                        sessionId: this.id, toolCallId: event.toolCallId, signal: this.toolAbort.signal,
                    });
                } catch (error) {
                    console.error(`Error executing tool "${event.toolName}" with call ID "${event.toolCallId}":`, error);
                    result = { ok: false, error: { code: 'TOOL_ERROR', message: 'Tool execution failed' } };
                }
            }
        }

        this.runningToolCalls.delete(event.toolCallId);
        if (!this.destroyed) {
            this.toolResults.set(event.toolCallId, result);
            this.sendCommand({ type: 'tool_result', toolCallId: event.toolCallId, result });
        }
    }
}
